import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
  Platform,
  FlatList,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as Location from 'expo-location';
import * as ImagePicker from 'expo-image-picker';
import { LinearGradient } from 'expo-linear-gradient';
import MapView, { Marker } from 'react-native-maps';

const { width, height } = Dimensions.get('window');
// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getAuthHeaders = async () => {
  const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) { try { const p = await AsyncStorage.getItem('company_profile'); if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; } } catch {} }
  return id || null;
};

const applyLogoUri = (l) => {
  if (!l || typeof l !== 'string') return null;
  if (l.startsWith('http') || l.startsWith('data:')) return l;
  if (l.startsWith('/')) return `${API_BASE_URL}${l}`;
  return null;
};


export default function VisitTrackingScreen({ navigation, route }) {
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId } = route?.params || {};

  // Company state
  const [companyId, setCompanyId] = useState(routeCompanyId || null);
  const [companyLogo, setCompanyLogo] = useState(applyLogoUri(routeCompanyProfile?.logo || routeCompanyProfile?.logo_url || routeCompanyProfile?.company_logo) || null);
  const [companyName, setCompanyName] = useState(routeCompanyProfile?.company_name || routeCompanyProfile?.name || '');

  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('visits'); // 'visits', 'clients', 'map', 'reports'
  
  // Visit data
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [visitPurposes, setVisitPurposes] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);

  // UI state
  const [showNewVisitModal, setShowNewVisitModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showVisitDetailsModal, setShowVisitDetailsModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);

  // Form state
  const [newVisit, setNewVisit] = useState({
    clientId: '',
    purpose: '',
    notes: '',
    location: null,
    address: '',
    photos: [],
    startTime: new Date(),
    endTime: null,
    status: 'planned', // 'planned', 'in-progress', 'completed', 'cancelled'
  });

  const [newClient, setNewClient] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    category: '',
    priority: 'medium',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationPermission, setLocationPermission] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(false);

  // Default visit purposes
  const defaultVisitPurposes = useMemo(() => [
    { id: 'meeting', name: 'Client Meeting', icon: 'account-group', color: '#4A90E2' },
    { id: 'inspection', name: 'Site Inspection', icon: 'magnify', color: '#10B981' },
    { id: 'delivery', name: 'Delivery', icon: 'truck-delivery', color: '#F59E0B' },
    { id: 'maintenance', name: 'Maintenance', icon: 'wrench', color: '#EF4444' },
    { id: 'consultation', name: 'Consultation', icon: 'chat', color: '#8B5CF6' },
    { id: 'training', name: 'Training', icon: 'school', color: '#06B6D4' },
    { id: 'audit', name: 'Audit', icon: 'clipboard-check', color: '#64748B' },
    { id: 'other', name: 'Other', icon: 'dots-horizontal', color: '#94A3B8' },
  ], []);

  // Initialize component
  useEffect(() => {
    initializeVisitTracking();
    requestPermissions();
  }, []);

  // Request permissions
  const requestPermissions = useCallback(async () => {
    try {
      // Location permission
      const { status: locationStatus } = await Location.requestForegroundPermissionsAsync();
      setLocationPermission(locationStatus === 'granted');

      // Camera permission
      const { status: cameraStatus } = await ImagePicker.requestCameraPermissionsAsync();
      setCameraPermission(cameraStatus === 'granted');

      if (locationStatus === 'granted') {
        getCurrentLocation();
      }
    } catch (error) {
      console.error('Permission request error:', error);
    }
  }, []);

  // Get current location
  const getCurrentLocation = useCallback(async () => {
    try {
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });
      setCurrentLocation(location.coords);
    } catch (error) {
      console.error('Location error:', error);
    }
  }, []);

  // Initialize visit tracking data
  const initializeVisitTracking = useCallback(async () => {
    try {
      setIsLoading(true);

      // Load visit purposes
      const savedPurposes = await AsyncStorage.getItem('visit_purposes');
      setVisitPurposes(savedPurposes ? JSON.parse(savedPurposes) : defaultVisitPurposes);

      // Load visits
      await loadVisits();
      
      // Load clients
      await loadClients();

    } catch (error) {
      console.error('Initialize visit tracking error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [defaultVisitPurposes]);

  // Load visits
  const loadVisits = useCallback(async () => {
    try {
      const visitsData = await AsyncStorage.getItem('visits');
      if (visitsData) {
        const parsed = JSON.parse(visitsData);
        // Sort by start time (newest first)
        parsed.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
        setVisits(parsed);
      } else {
        // Initialize with sample data
        const sampleVisits = [
          {
            id: '1',
            clientId: '1',
            purpose: 'meeting',
            notes: 'Quarterly review meeting with client',
            location: { latitude: 51.5074, longitude: -0.1278 },
            address: '123 Business Street, London, UK',
            photos: [],
            startTime: '2024-12-01T10:00:00Z',
            endTime: '2024-12-01T11:30:00Z',
            status: 'completed',
            duration: 90, // minutes
          },
          {
            id: '2',
            clientId: '2',
            purpose: 'inspection',
            notes: 'Site inspection for new project',
            location: { latitude: 51.5155, longitude: -0.1426 },
            address: '456 Construction Ave, London, UK',
            photos: [],
            startTime: '2024-12-02T14:00:00Z',
            endTime: null,
            status: 'planned',
            duration: null,
          },
        ];
        setVisits(sampleVisits);
        await AsyncStorage.setItem('visits', JSON.stringify(sampleVisits));
      }
    } catch (error) {
      console.error('Load visits error:', error);
    }
  }, []);

  // Load clients
  const loadClients = useCallback(async () => {
    try {
      const clientsData = await AsyncStorage.getItem('clients');
      if (clientsData) {
        setClients(JSON.parse(clientsData));
      } else {
        // Initialize with sample data
        const sampleClients = [
          {
            id: '1',
            name: 'John Smith',
            company: 'ABC Construction Ltd',
            email: 'john@abcconstruction.com',
            phone: '+44 7700 900123',
            address: '123 Business Street, London, UK',
            notes: 'Primary contact for ABC Construction projects',
            category: 'construction',
            priority: 'high',
            lastVisit: '2024-12-01T10:00:00Z',
            totalVisits: 5,
          },
          {
            id: '2',
            name: 'Sarah Johnson',
            company: 'XYZ Engineering',
            email: 'sarah@xyzeng.com',
            phone: '+44 7700 900124',
            address: '456 Construction Ave, London, UK',
            notes: 'Engineering consultant for major projects',
            category: 'engineering',
            priority: 'medium',
            lastVisit: '2024-11-28T14:00:00Z',
            totalVisits: 3,
          },
        ];
        setClients(sampleClients);
        await AsyncStorage.setItem('clients', JSON.stringify(sampleClients));
      }
    } catch (error) {
      console.error('Load clients error:', error);
    }
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await getCurrentLocation();
      await initializeVisitTracking();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  }, [getCurrentLocation, initializeVisitTracking]);

  // Start visit
  const startVisit = useCallback(async (visitId) => {
    try {
      if (!locationPermission) {
        Alert.alert('Permission Required', 'Location permission is required to start a visit.');
        return;
      }

      // Get current location
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });

      // Get address from coordinates
      const address = await Location.reverseGeocodeAsync({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      });

      const updatedVisits = visits.map(visit => 
        visit.id === visitId 
          ? {
              ...visit,
              status: 'in-progress',
              startTime: new Date().toISOString(),
              location: location.coords,
              address: address[0] ? `${address[0].street}, ${address[0].city}` : 'Unknown location',
            }
          : visit
      );

      setVisits(updatedVisits);
      await AsyncStorage.setItem('visits', JSON.stringify(updatedVisits));

      Alert.alert('Success', 'Visit started successfully!');

    } catch (error) {
      console.error('Start visit error:', error);
      Alert.alert('Error', 'Failed to start visit. Please try again.');
    }
  }, [visits, locationPermission]);

  // End visit
  const endVisit = useCallback(async (visitId) => {
    try {
      const visit = visits.find(v => v.id === visitId);
      if (!visit) return;

      const startTime = new Date(visit.startTime);
      const endTime = new Date();
      const duration = Math.round((endTime - startTime) / (1000 * 60)); // minutes

      const updatedVisits = visits.map(v => 
        v.id === visitId 
          ? {
              ...v,
              status: 'completed',
              endTime: endTime.toISOString(),
              duration,
            }
          : v
      );

      setVisits(updatedVisits);
      await AsyncStorage.setItem('visits', JSON.stringify(updatedVisits));

      // Update client's last visit
      const client = clients.find(c => c.id === visit.clientId);
      if (client) {
        const updatedClients = clients.map(c => 
          c.id === visit.clientId 
            ? {
                ...c,
                lastVisit: endTime.toISOString(),
                totalVisits: (c.totalVisits || 0) + 1,
              }
            : c
        );
        setClients(updatedClients);
        await AsyncStorage.setItem('clients', JSON.stringify(updatedClients));
      }

      Alert.alert('Success', `Visit completed! Duration: ${Math.floor(duration / 60)}h ${duration % 60}m`);

    } catch (error) {
      console.error('End visit error:', error);
      Alert.alert('Error', 'Failed to end visit. Please try again.');
    }
  }, [visits, clients]);

  // Add new visit
  const addNewVisit = useCallback(async () => {
    try {
      // Validation
      if (!newVisit.clientId) {
        Alert.alert('Error', 'Please select a client');
        return;
      }

      if (!newVisit.purpose) {
        Alert.alert('Error', 'Please select a visit purpose');
        return;
      }

      setIsSubmitting(true);

      const visit = {
        id: Date.now().toString(),
        ...newVisit,
        startTime: newVisit.startTime.toISOString(),
        endTime: newVisit.endTime ? newVisit.endTime.toISOString() : null,
        location: currentLocation,
        address: newVisit.address || 'Current location',
      };

      const updatedVisits = [visit, ...visits];
      setVisits(updatedVisits);
      await AsyncStorage.setItem('visits', JSON.stringify(updatedVisits));

      // Sync to server
      await syncVisitToServer(visit);

      // Reset form
      setNewVisit({
        clientId: '',
        purpose: '',
        notes: '',
        location: null,
        address: '',
        photos: [],
        startTime: new Date(),
        endTime: null,
        status: 'planned',
      });

      setShowNewVisitModal(false);
      Alert.alert('Success', 'Visit scheduled successfully!');

    } catch (error) {
      console.error('Add new visit error:', error);
      Alert.alert('Error', 'Failed to schedule visit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [newVisit, visits, currentLocation]);

  // Add new client
  const addNewClient = useCallback(async () => {
    try {
      // Validation
      if (!newClient.name.trim()) {
        Alert.alert('Error', 'Please enter client name');
        return;
      }

      if (!newClient.company.trim()) {
        Alert.alert('Error', 'Please enter company name');
        return;
      }

      setIsSubmitting(true);

      const client = {
        id: Date.now().toString(),
        ...newClient,
        lastVisit: null,
        totalVisits: 0,
      };

      const updatedClients = [client, ...clients];
      setClients(updatedClients);
      await AsyncStorage.setItem('clients', JSON.stringify(updatedClients));

      // Sync to server
      await syncClientToServer(client);

      // Reset form
      setNewClient({
        name: '',
        company: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
        category: '',
        priority: 'medium',
      });

      setShowClientModal(false);
      Alert.alert('Success', 'Client added successfully!');

    } catch (error) {
      console.error('Add new client error:', error);
      Alert.alert('Error', 'Failed to add client. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [newClient, clients]);

  // Sync visit to server
  const syncVisitToServer = useCallback(async (visit) => {
    try {
      // This would sync to your backend server
      console.log('Syncing visit to server:', visit);
    } catch (error) {
      console.error('Sync visit to server error:', error);
    }
  }, []);

  // Sync client to server
  const syncClientToServer = useCallback(async (client) => {
    try {
      // This would sync to your backend server
      console.log('Syncing client to server:', client);
    } catch (error) {
      console.error('Sync client to server error:', error);
    }
  }, []);

  // Capture photo
  const capturePhoto = useCallback(async () => {
    try {
      if (!cameraPermission) {
        Alert.alert('Permission Required', 'Camera permission is required to take photos.');
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setNewVisit(prev => ({
          ...prev,
          photos: [...prev.photos, result.assets[0].uri],
        }));
      }
    } catch (error) {
      console.error('Photo capture error:', error);
      Alert.alert('Error', 'Failed to capture photo. Please try again.');
    }
  }, [cameraPermission]);

  // Get status color
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'completed':
        return '#10B981';
      case 'in-progress':
        return '#4A90E2';
      case 'planned':
        return '#F59E0B';
      case 'cancelled':
        return '#EF4444';
      default:
        return '#64748B';
    }
  }, []);

  // Get priority color
  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case 'high':
        return '#EF4444';
      case 'medium':
        return '#F59E0B';
      case 'low':
        return '#10B981';
      default:
        return '#64748B';
    }
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  // Format time
  const formatTime = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }, []);

  // Render visit item
  const renderVisitItem = useCallback(({ item }) => {
    const client = clients.find(c => c.id === item.clientId);
    const purpose = visitPurposes.find(p => p.id === item.purpose);
    
    return (
      <TouchableOpacity
        style={styles.visitCard}
        onPress={() => {
          setSelectedVisit(item);
          setShowVisitDetailsModal(true);
        }}
      >
        <View style={styles.visitHeader}>
          <View style={styles.visitClient}>
            <MaterialCommunityIcons 
              name={purpose?.icon || 'calendar-outline'} 
              size={24} 
              color={purpose?.color || '#4A90E2'} 
            />
            <View style={styles.visitClientInfo}>
              <Text style={styles.visitClientName}>{client?.name || 'Unknown Client'}</Text>
              <Text style={styles.visitClientCompany}>{client?.company || ''}</Text>
            </View>
          </View>
          
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
            <Text style={styles.statusText}>{String(item.status ?? '').toUpperCase()}</Text>
          </View>
        </View>
        
        <View style={styles.visitDetails}>
          <Text style={styles.visitPurpose}>{purpose?.name || 'Unknown Purpose'}</Text>
          <Text style={styles.visitAddress} numberOfLines={1}>{item.address}</Text>
          <Text style={styles.visitTime}>
            {formatDate(item.startTime)} at {formatTime(item.startTime)}
          </Text>
          
          {item.duration && (
            <Text style={styles.visitDuration}>
              Duration: {Math.floor(item.duration / 60)}h {item.duration % 60}m
            </Text>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.visitActions}>
          {item.status === 'planned' && (
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: '#4A90E2' }]}
              onPress={() => startVisit(item.id)}
            >
              <MaterialCommunityIcons name="play" size={16} color="#FFFFFF" />
              <Text style={styles.actionButtonText}>Start</Text>
            </TouchableOpacity>
          )}
          
          {item.status === 'in-progress' && (
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: '#10B981' }]}
              onPress={() => endVisit(item.id)}
            >
              <MaterialCommunityIcons name="stop" size={16} color="#FFFFFF" />
              <Text style={styles.actionButtonText}>End</Text>
            </TouchableOpacity>
          )}
          
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: '#64748B' }]}
            onPress={() => {
              setSelectedVisit(item);
              setShowMapModal(true);
            }}
          >
            <MaterialCommunityIcons name="map" size={16} color="#FFFFFF" />
            <Text style={styles.actionButtonText}>Map</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  }, [clients, visitPurposes, getStatusColor, formatDate, formatTime, startVisit, endVisit]);

  // Render client item
  const renderClientItem = useCallback(({ item }) => {
    return (
      <TouchableOpacity
        style={styles.clientCard}
        onPress={() => {
          setSelectedClient(item);
          // Could open client details modal
        }}
      >
        <View style={styles.clientHeader}>
          <View style={styles.clientInfo}>
            <Text style={styles.clientName}>{item.name}</Text>
            <Text style={styles.clientCompany}>{item.company}</Text>
          </View>
          
          <View style={[styles.priorityBadge, { backgroundColor: getPriorityColor(item.priority) }]}>
            <Text style={styles.priorityText}>{String(item.priority ?? '').toUpperCase()}</Text>
          </View>
        </View>
        
        <View style={styles.clientDetails}>
          <View style={styles.clientContact}>
            <MaterialCommunityIcons name="email" size={16} color="#64748B" />
            <Text style={styles.clientContactText}>{item.email}</Text>
          </View>
          
          <View style={styles.clientContact}>
            <MaterialCommunityIcons name="phone" size={16} color="#64748B" />
            <Text style={styles.clientContactText}>{item.phone}</Text>
          </View>
          
          <View style={styles.clientStats}>
            <Text style={styles.clientStat}>
              Last Visit: {item.lastVisit ? formatDate(item.lastVisit) : 'Never'}
            </Text>
            <Text style={styles.clientStat}>
              Total Visits: {item.totalVisits || 0}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [getPriorityColor, formatDate]);

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'visits':
        return (
          <FlatList
            data={visits}
            renderItem={renderVisitItem}
            keyExtractor={(item) => item.id}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                colors={['#4A90E2']}
              />
            }
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="map-marker-outline" size={64} color="#94A3B8" />
                <Text style={styles.emptyStateTitle}>No Visits</Text>
                <Text style={styles.emptyStateDescription}>
                  You haven't scheduled any visits yet
                </Text>
              </View>
            }
          />
        );

      case 'clients':
        return (
          <FlatList
            data={clients}
            renderItem={renderClientItem}
            keyExtractor={(item) => item.id}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.listContainer}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                colors={['#4A90E2']}
              />
            }
            ListEmptyComponent={
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="account-group" size={64} color="#94A3B8" />
                <Text style={styles.emptyStateTitle}>No Clients</Text>
                <Text style={styles.emptyStateDescription}>
                  You haven't added any clients yet
                </Text>
              </View>
            }
          />
        );

      case 'map':
        return (
          <View style={styles.mapContainer}>
            {currentLocation ? (
              <MapView
                style={styles.map}
                initialRegion={{
                  latitude: currentLocation.latitude,
                  longitude: currentLocation.longitude,
                  latitudeDelta: 0.0922,
                  longitudeDelta: 0.0421,
                }}
                showsUserLocation={true}
                showsMyLocationButton={true}
              >
                {visits
                  .filter(visit => visit.location)
                  .map((visit) => {
                    const client = clients.find(c => c.id === visit.clientId);
                    const purpose = visitPurposes.find(p => p.id === visit.purpose);
                    
                    return (
                      <Marker
                        key={visit.id}
                        coordinate={visit.location}
                        title={client?.name || 'Unknown Client'}
                        description={purpose?.name || 'Unknown Purpose'}
                        pinColor={getStatusColor(visit.status)}
                      />
                    );
                  })}
              </MapView>
            ) : (
              <View style={styles.mapPlaceholder}>
                <MaterialCommunityIcons name="map-outline" size={64} color="#94A3B8" />
                <Text style={styles.mapPlaceholderText}>Loading map...</Text>
              </View>
            )}
          </View>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading Visit Tracking...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient
        colors={['#0e4e96', '#1e6bc2']}
        style={styles.header}
      >
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 28, height: 28, borderRadius: 14, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} /> : null}
          <View>
            <Text style={styles.headerTitle}>Visit Tracking</Text>
            {companyName ? <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>{companyName}</Text> : null}
          </View>
        </View>
        
        <View style={{ flexDirection: 'row', gap: 4 }}>
          <TouchableOpacity style={styles.addButton} onPress={() => Alert.alert('Export', 'Choose format', [{ text: 'Cancel', style: 'cancel' }, { text: 'PDF', onPress: exportVisitsPDF }, { text: 'Print', onPress: printVisits }])}>
            <Ionicons name="download-outline" size={22} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.addButton} onPress={() => activeTab === 'clients' ? setShowClientModal(true) : setShowNewVisitModal(true)}>
            <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {[
            { key: 'visits', label: 'Visits', icon: 'map-marker-outline' },
            { key: 'clients', label: 'Clients', icon: 'account-group' },
            { key: 'map', label: 'Map View', icon: 'map-outline' },
            { key: 'reports', label: 'Reports', icon: 'chart-bar' },
          ].map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === tab.key && styles.activeTab]}
              onPress={() => setActiveTab(tab.key)}
            >
              <MaterialCommunityIcons
                name={tab.icon}
                size={20}
                color={activeTab === tab.key ? '#4A90E2' : '#64748B'}
              />
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.key && styles.activeTabText,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      <View style={styles.content}>
        {renderTabContent()}
      </View>

      {/* New Visit Modal */}
      <Modal
        visible={showNewVisitModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowNewVisitModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Schedule Visit</Text>
            <TouchableOpacity onPress={() => setShowNewVisitModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            {/* Client Selection */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Client *</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.clientOptions}>
                  {clients.map((client) => (
                    <TouchableOpacity
                      key={client.id}
                      style={[
                        styles.clientOption,
                        newVisit.clientId === client.id && styles.clientOptionSelected,
                      ]}
                      onPress={() => setNewVisit(prev => ({ ...prev, clientId: client.id }))}
                    >
                      <Text style={[
                        styles.clientOptionName,
                        newVisit.clientId === client.id && styles.clientOptionNameSelected,
                      ]}>
                        {client.name}
                      </Text>
                      <Text style={styles.clientOptionCompany}>{client.company}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Purpose Selection */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Purpose *</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.purposeOptions}>
                  {visitPurposes.map((purpose) => (
                    <TouchableOpacity
                      key={purpose.id}
                      style={[
                        styles.purposeOption,
                        newVisit.purpose === purpose.id && styles.purposeOptionSelected,
                        { borderColor: purpose.color }
                      ]}
                      onPress={() => setNewVisit(prev => ({ ...prev, purpose: purpose.id }))}
                    >
                      <MaterialCommunityIcons 
                        name={purpose.icon} 
                        size={24} 
                        color={newVisit.purpose === purpose.id ? purpose.color : '#64748B'} 
                      />
                      <Text style={[
                        styles.purposeOptionText,
                        newVisit.purpose === purpose.id && { color: purpose.color }
                      ]}>
                        {purpose.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Address */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Address</Text>
              <TextInput
                style={styles.input}
                value={newVisit.address}
                onChangeText={(text) => setNewVisit(prev => ({ ...prev, address: text }))}
                placeholder="Visit address (optional - will use current location)"
                multiline
              />
            </View>

            {/* Notes */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Notes</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={newVisit.notes}
                onChangeText={(text) => setNewVisit(prev => ({ ...prev, notes: text }))}
                placeholder="Visit notes and objectives"
                multiline
                numberOfLines={4}
              />
            </View>

            {/* Photos */}
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Photos</Text>
              <TouchableOpacity style={styles.photoButton} onPress={capturePhoto}>
                <MaterialCommunityIcons name="camera" size={24} color="#4A90E2" />
                <Text style={styles.photoButtonText}>Add Photo</Text>
              </TouchableOpacity>
              
              {newVisit.photos.length > 0 && (
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.photoPreview}>
                    {newVisit.photos.map((photo, index) => (
                      <Image key={index} source={{ uri: (photo && photo.startsWith('http')) ? photo : undefined }} style={styles.photoThumbnail} />
                    ))}
                  </View>
                </ScrollView>
              )}
            </View>

            {/* Submit Button */}
            <TouchableOpacity
              style={[styles.submitButton, (!newVisit.clientId || !newVisit.purpose) && styles.submitButtonDisabled]}
              onPress={addNewVisit}
              disabled={isSubmitting || !newVisit.clientId || !newVisit.purpose}
            >
              {isSubmitting ? (
                <ActivityIndicator size={20} color="#FFFFFF" />
              ) : (
                <>
                  <MaterialCommunityIcons name="calendar-plus" size={20} color="#FFFFFF" />
                  <Text style={styles.submitButtonText}>Schedule Visit</Text>
                </>
              )}
            </TouchableOpacity>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* New Client Modal */}
      <Modal
        visible={showClientModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowClientModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Add Client</Text>
            <TouchableOpacity onPress={() => setShowClientModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Client Name *</Text>
              <TextInput
                style={styles.input}
                value={newClient.name}
                onChangeText={(text) => setNewClient(prev => ({ ...prev, name: text }))}
                placeholder="Enter client name"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Company *</Text>
              <TextInput
                style={styles.input}
                value={newClient.company}
                onChangeText={(text) => setNewClient(prev => ({ ...prev, company: text }))}
                placeholder="Enter company name"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Email</Text>
              <TextInput
                style={styles.input}
                value={newClient.email}
                onChangeText={(text) => setNewClient(prev => ({ ...prev, email: text }))}
                placeholder="Enter email address"
                keyboardType="email-address"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Phone</Text>
              <TextInput
                style={styles.input}
                value={newClient.phone}
                onChangeText={(text) => setNewClient(prev => ({ ...prev, phone: text }))}
                placeholder="Enter phone number"
                keyboardType="phone-pad"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Address</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={newClient.address}
                onChangeText={(text) => setNewClient(prev => ({ ...prev, address: text }))}
                placeholder="Enter client address"
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Priority</Text>
              <View style={styles.priorityOptions}>
                {['high', 'medium', 'low'].map((priority) => (
                  <TouchableOpacity
                    key={priority}
                    style={[
                      styles.priorityOption,
                      newClient.priority === priority && styles.priorityOptionSelected,
                      { borderColor: getPriorityColor(priority) }
                    ]}
                    onPress={() => setNewClient(prev => ({ ...prev, priority }))}
                  >
                    <Text style={[
                      styles.priorityOptionText,
                      newClient.priority === priority && { color: getPriorityColor(priority) }
                    ]}>
                      {priority.toUpperCase()}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Notes</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={newClient.notes}
                onChangeText={(text) => setNewClient(prev => ({ ...prev, notes: text }))}
                placeholder="Additional notes about the client"
                multiline
                numberOfLines={3}
              />
            </View>

            <TouchableOpacity
              style={[styles.submitButton, (!newClient.name.trim() || !newClient.company.trim()) && styles.submitButtonDisabled]}
              onPress={addNewClient}
              disabled={isSubmitting || !newClient.name.trim() || !newClient.company.trim()}
            >
              {isSubmitting ? (
                <ActivityIndicator size={20} color="#FFFFFF" />
              ) : (
                <>
                  <MaterialCommunityIcons name="account-plus" size={20} color="#FFFFFF" />
                  <Text style={styles.submitButtonText}>Add Client</Text>
                </>
              )}
            </TouchableOpacity>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Visit Details Modal */}
      <Modal
        visible={showVisitDetailsModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowVisitDetailsModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Visit Details</Text>
            <TouchableOpacity onPress={() => setShowVisitDetailsModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          {selectedVisit && (
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {/* Visit details content would go here */}
              <Text style={styles.detailsText}>Visit details for {selectedVisit.id}</Text>
            </ScrollView>
          )}
        </SafeAreaView>
      </Modal>

      {/* Map Modal */}
      <Modal
        visible={showMapModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowMapModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Visit Location</Text>
            <TouchableOpacity onPress={() => setShowMapModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          {selectedVisit && selectedVisit.location && (
            <MapView
              style={styles.modalMap}
              initialRegion={{
                latitude: selectedVisit.location.latitude,
                longitude: selectedVisit.location.longitude,
                latitudeDelta: 0.01,
                longitudeDelta: 0.01,
              }}
            >
              <Marker
                coordinate={selectedVisit.location}
                title={clients.find(c => c.id === selectedVisit.clientId)?.name || 'Visit Location'}
                description={selectedVisit.address}
              />
            </MapView>
          )}
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 50 : 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  addButton: {
    padding: 8,
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginRight: 8,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#4A90E2',
  },
  tabText: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#4A90E2',
  },
  content: {
    flex: 1,
  },
  listContainer: {
    padding: 20,
  },
  visitCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  visitHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  visitClient: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  visitClientInfo: {
    marginLeft: 12,
  },
  visitClientName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  visitClientCompany: {
    fontSize: 12,
    color: '#64748B',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  visitDetails: {
    marginBottom: 16,
  },
  visitPurpose: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0F172A',
    marginBottom: 4,
  },
  visitAddress: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  visitTime: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  visitDuration: {
    fontSize: 12,
    color: '#10B981',
    fontWeight: '500',
  },
  visitActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  clientCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  clientHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  clientInfo: {
    flex: 1,
  },
  clientName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  clientCompany: {
    fontSize: 14,
    color: '#64748B',
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  clientDetails: {
    gap: 8,
  },
  clientContact: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  clientContactText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 8,
  },
  clientStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  clientStat: {
    fontSize: 12,
    color: '#64748B',
  },
  mapContainer: {
    flex: 1,
  },
  map: {
    flex: 1,
  },
  mapPlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mapPlaceholderText: {
    fontSize: 16,
    color: '#64748B',
    marginTop: 16,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
  },
  emptyStateDescription: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalContent: {
    padding: 20,
  },
  modalMap: {
    flex: 1,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  clientOptions: {
    flexDirection: 'row',
    gap: 12,
  },
  clientOption: {
    padding: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    minWidth: 120,
  },
  clientOptionSelected: {
    borderColor: '#4A90E2',
    backgroundColor: '#F0F7FF',
  },
  clientOptionName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0F172A',
  },
  clientOptionNameSelected: {
    color: '#4A90E2',
  },
  clientOptionCompany: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
  purposeOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  purposeOption: {
    alignItems: 'center',
    padding: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    minWidth: 100,
  },
  purposeOptionSelected: {
    backgroundColor: '#F0F7FF',
  },
  purposeOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
    marginTop: 4,
    textAlign: 'center',
  },
  priorityOptions: {
    flexDirection: 'row',
    gap: 12,
  },
  priorityOption: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  priorityOptionSelected: {
    backgroundColor: '#F0F7FF',
  },
  priorityOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
  },
  photoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderWidth: 2,
    borderColor: '#4A90E2',
    borderStyle: 'dashed',
    borderRadius: 8,
    backgroundColor: '#F0F7FF',
  },
  photoButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4A90E2',
    marginLeft: 8,
  },
  photoPreview: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  photoThumbnail: {
    width: 60,
    height: 60,
    borderRadius: 8,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#4A90E2',
    borderRadius: 12,
    paddingVertical: 16,
    marginTop: 20,
  },
  submitButtonDisabled: {
    backgroundColor: '#94A3B8',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  detailsText: {
    fontSize: 16,
    color: '#0F172A',
  },
});